import { useState } from 'react'
import { Box, CircularProgress, Collapse, IconButton, Tooltip, Typography } from '@mui/material'
import { FancyButton } from './FancyButtons'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import EvalCategorySection from './EvalCategorySection'
import EvalGroupSection from './EvalGroupSection'
import TAChip from './TAChip'
import { FS_SM, ICON_SM, ICON_XS } from '../common/theme'
import { hasAnswerText } from '../common/common'

interface TaEvalImprovementsProps {
  improvements: any[]
  question?: any
  onImprove?: () => void
  onFixAll?: () => void
  onApplyFix?: (fix: any) => void
  onDeleteFix?: (fix: any) => void
  onClearFixes?: () => void
}

function ImprovementRow({ q, onApplyFix, onDeleteFix }: {
  q: any
  onApplyFix?: (fix: any) => void
  onDeleteFix?: (fix: any) => void
}) {
  const [showDetail, setShowDetail] = useState(true)

  const typeLabel = q.improvtypestring === 'addanswer'
    ? 'Add to your answer'
    : q.improvtypestring === 'addsource'
      ? 'Add source'
      : 'No suggestion'

  return (
    <Box sx={{ pl: 1.5, borderBottom: 1, borderColor: 'divider', py: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        {q.improvtypestring ?
          <TAChip kind="fixable" />
          : <TAChip kind="notfixable" />
        }
        <Typography variant="caption" sx={{ flex: 1, fontSize: FS_SM, lineHeight: 1.4 }}>
          {q.checkpoint}
        </Typography>
        {q.improvtypestring ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>

            {!q.fix ? (
              <Tooltip title="Apply Fix">
                <IconButton size="small" onClick={() => onApplyFix?.(q)} sx={{ p: 0.2, color: 'primary.main' }}>
                  <BuildOutlinedIcon sx={{ fontSize: ICON_XS }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Remove Fix">
                <IconButton size="small" onClick={() => onDeleteFix?.(q)} sx={{ p: 0.2 }}>
                  <DeleteOutlineIcon sx={{ fontSize: ICON_XS }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ) : (
          <TAChip kind="notfixable" />
        )}
        <IconButton size="small" onClick={() => setShowDetail(v => !v)} sx={{ p: 0.2, flexShrink: 0 }}>
          {showDetail ? <ExpandLessIcon sx={{ fontSize: ICON_SM }} /> : <ExpandMoreIcon sx={{ fontSize: ICON_SM }} />}
        </IconButton>
      </Box>

      <Collapse in={showDetail}>
        <Box sx={{ mt: 0.25, pl: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: FS_SM, fontWeight: 600, opacity: 0.5, display: 'block', mb: 0.25 }}>
            {typeLabel}
          </Typography>
          {q.improvtypestring === 'addsource' && q.filename && (
            <Typography variant="caption" sx={{ fontSize: FS_SM, opacity: 0.9, display: 'block', mb: 0.25 }}>
              From: <span dangerouslySetInnerHTML={{ __html: q.filename }} />
            </Typography>
          )}
          {q.improvtypestring === 'addanswer' && (
            <Typography variant="caption" sx={{ fontSize: FS_SM, opacity: 0.9, display: 'block', mb: 0.25 }}>
              Text was generated from your current sources
            </Typography>
          )}
          {q.suggestion && (
            <Typography variant="caption" sx={{ display: 'block', fontSize: FS_SM, opacity: 0.8, lineHeight: 1.4 }}>
              {q.suggestion}
            </Typography>
          )}
          {!q.improvtypestring && (
            <Typography variant="caption" sx={{ fontSize: FS_SM, opacity: 0.9 }}>
              Unfortunately we couldn't automatically suggest a way to improve your answer
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

function CategoryImprovements({ c, onApplyFix, onDeleteFix }: {
  c: any
  onApplyFix?: (fix: any) => void
  onDeleteFix?: (fix: any) => void
}) {
  if (!c.improvements || c.improvements.length === 0) return null
  return (
    <EvalCategorySection name={c.category}>
      {(c.improvements ?? []).map((q: any, qi: number) => (
        <ImprovementRow key={q.id ?? qi} q={q} onApplyFix={onApplyFix} onDeleteFix={onDeleteFix} />
      ))}
    </EvalCategorySection>
  )
}

export default function TaEvalImprovements({
  question,
  onImprove,
  onFixAll,
  onApplyFix,
  onDeleteFix,
  onClearFixes,
}: TaEvalImprovementsProps) {
  const groups = question?.bideval?.checkpointeval?.groups ?? []
  const scoreInt = question?.bideval?.scoreint
  const isProcessing = typeof scoreInt === 'number' && scoreInt < 0
  const allImprovements = groups.flatMap((g: any) =>
    (g.improvementcategories ?? []).flatMap((c: any) => c.improvements ?? [])
  )
  const anyFixes = allImprovements.length > 0
  const fixesSelected = allImprovements.filter((q: any) => q.fix).length
  const isPerfect = question?.bideval?.scoreint === 100
  const hasAnswer = hasAnswerText(question)
  const isScored = typeof question?.bideval?.scoreint === 'number' && question.bideval.scoreint >= 0

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 0.75, py: 0.5, borderBottom: 1, borderColor: 'divider', gap: 0.5 }}>
        <Typography variant="caption" sx={{ opacity: 0.55, fontSize: '0.7rem' }}>Improvements</Typography>
        {fixesSelected > 0 && (
          <TAChip
            kind="selected"
            label={`${fixesSelected} selected`}
            onDelete={onClearFixes}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {isProcessing && <CircularProgress size={12} />}
        {anyFixes && (
          <Tooltip title="Apply All Fixes">
            <IconButton size="small" onClick={onFixAll} sx={{ p: 0.25, color: 'primary.main' }}>
              <BuildOutlinedIcon sx={{ fontSize: ICON_SM }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {!anyFixes && !isPerfect && (
        <Box sx={{ px: 0.75, py: 0.75 }}>
          <FancyButton
            disabled={!hasAnswer || !isScored}
            onClick={onImprove}
          >
            Suggest Improvements
          </FancyButton>
        </Box>
      )}

      {isPerfect && !anyFixes && (
        <Box sx={{ px: 1, py: 1 }}>
          <Typography variant="caption" sx={{ opacity: 0.5, fontSize: FS_SM }}>
            Perfect score. No improvements needed.
          </Typography>
        </Box>
      )}

      <Box sx={{ p: 0.75 }}>
        {groups.map((g: any, gi: number) => {
          const hasCategories = !!g.improvementcategories && g.improvementcategories.length > 0
          return (
            <EvalGroupSection
              key={g.id ?? gi}
              name={g.name}
              emptyMessage={hasCategories ? undefined : 'None'}
            >
              {(g.improvementcategories ?? []).map((c: any, ci: number) => (
                <CategoryImprovements key={c.id ?? ci} c={c} onApplyFix={onApplyFix} onDeleteFix={onDeleteFix} />
              ))}
            </EvalGroupSection>
          )
        })}
      </Box>
    </Box>
  )
}
